import React, { Component, Fragment } from "reactn";
import moment from "moment";
import { Icon } from "@material-ui/core";

import TransactionsTable from "../Billing/TransactionsTable";
import { enableTestingApi } from "js/config";
import Api from "js/core/api";
import {
    BlueButton,
    DarkButton,
    UIPane,
    UIPaneContents,
    UIPaneHeader,
    YellowButton
} from "js/react/components/UiComponents";
import Loadable from "../../../components/Loadable";
import { ShowConfirmationDialog, ShowDialog, ShowErrorDialog } from "../../../components/Dialogs/BaseDialog";
import ChangePaymentMethodDialog from "../Billing/ChangePaymentMethodDialog";
import { PaymentMethod } from "../Billing/PaymentMethod";
import { app } from "js/namespaces";
import { Gap20 } from "../../../components/Gap";
import BillingDataService from "../dataservices/billingDataService";
import { DowngradePlanDialog } from "js/react/views/UserOptions/Billing/DowngradePlanDialog";
import log from "js/core/logger";
import { getPricingPageUrl } from "js/core/utilities/pricing";
import { ds } from "js/core/models/dataService";
import { NextPaymentSection } from "js/react/views/UserOptions/components/NextPaymentSection";
import { APP_UNLIMITED_SLIDE_COUNT, CustomerType } from "common/constants";
import ChangePlanDialog from "../dialogs/ChangePlanDialog";

import "css/billing.scss";

class BillingPane extends Component {
    initialState = {
        isLoading: true,
        subscription: null,
        managedTeam: null,
        transactionHistoryData: [],
        availableReferralCredit: 0
    };

    state = { ...this.initialState };

    get hasSubscription() {
        let { subscription } = this.state;
        return subscription != null && subscription.product != "basic" && subscription.id != null;
    }

    get isInTrial() {
        let { subscription } = this.state;
        return subscription && subscription.status == "trialing";
    }

    get nextInterval() {
        let { subscription } = this.state;
        let nextPhase = null;
        if (subscription.schedule) {
            const currentPhaseStateDate = subscription.schedule.current_phase.start_date;
            const currentPhaseIndex = subscription.schedule.phases.findIndex(phase => phase.start_date == currentPhaseStateDate);
            if (subscription.schedule.phases.length > 1) {
                nextPhase = subscription.schedule.phases[currentPhaseIndex + 1];
            }
        }

        let nextInterval = subscription.interval;
        if (nextPhase) {
            nextInterval = nextPhase.plans[0].plan.interval;
        }

        return nextInterval;
    }

    get isBillingManagedByTeam() {
        return this.state.managedTeam != null;
    }

    get canceledSubscription() {
        let { subscription } = this.state;
        return subscription.cancel_at_period_end;
    }

    async componentDidMount() {
        await this.loadBillingData();
    }

    async componentDidUpdate(prevProps) {
        if (prevProps.organizationId !== this.props.organizationId) {
            this.setState(this.initialState, async () => {
                await this.loadBillingData();
            });
        }
    }

    async fetchManagedTeam() {
        await ds.teams.loadModels();
        let userTeams = ds.teams.models;
        if (!userTeams) return;
        let managedTeam;

        for (let team of userTeams) {
            const teamMembers = team.get("members");
            if (teamMembers[app.user.id] && teamMembers[app.user.id].hasSeat) {
                managedTeam = team;
                break;
            }
        }

        if (managedTeam) {
            this.setState({
                managedTeam: managedTeam.get("name")
            });
        }
    }

    async loadBillingData() {
        this.setState({ isLoading: true });
        this.setState({
            paymentMethod: await BillingDataService.fetchPaymentMethod(this.props.organizationId),
            quota: await BillingDataService.fetchQuotas(this.props.organizationId),
            subscription: await BillingDataService.fetchSubscription(this.props.organizationId)
        });

        await this.fetchManagedTeam();
        await this.getTransactionHistory();
        await this.getReferralCredit();

        this.setState({ isLoading: false });
    }

    getTransactionHistory = async () => {
        try {
            const transactionHistoryData = await Api.transactions.get({ orgId: this.props.organizationId });
            this.setState({ transactionHistoryData: transactionHistoryData.body });
        } catch (err) {
            log.error(err);
        }
    };

    getReferralCredit = async () => {
        try {
            const data = await Api.referrals.get();
            this.setState({ availableReferralCredit: data.totalAvailable });
        } catch (err) {
            log.error(err);
        }
    }

    handleChangePaymentMethod = async () => {
        ShowDialog(ChangePaymentMethodDialog, {
            orgId: this.props.organizationId,
            callback: () => this.loadBillingData()
        });
    };

    handleEndTrialNow = async () => {
        // for QA and dev, only avaialble during when testing api is enabled
        ShowConfirmationDialog({
            title: `End trial now?`,
            message: "***** Testing Only *****",
            cancelButtonLabel: "Nope",
            okButtonLabel: "End now",
        }).then(async accept => {
            if (!accept) {
                return;
            }
            document.body.style.cursor = "progress";
            const orgId = this.props.organizationId;

            const res = await fetch(`/testing/stripe/end-${orgId ? "team" : "pro"}-trial`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json",
                },
                body: JSON.stringify(orgId
                    ? { orgId }
                    : { email: app.user.getAuthUser().email }
                )
            });
            document.body.style.cursor = "default";
            const data = await res.json();
            alert("Result = " + JSON.stringify(data));
        });
    };

    handleCancelSubscription = async event => {
        if (enableTestingApi && this.isInTrial && event.shiftKey) {
            this.handleEndTrialNow();
            return;
        }

        const { subscription } = this.state;
        const nextBillDate = moment.unix(subscription.current_period_end).format("MMM DD, YYYY");
        const isOrganization = this.props.organizationId !== undefined;
        const isInDunning = subscription.status === "past_due";
        let message;
        if (!isOrganization) {
            if (isInDunning) {
                message = (<Fragment>
                    You will be immediately downgraded to the Basic plan.
                </Fragment>);
            } else {
                message = (<Fragment>
                    Your plan will remain active until the end of your current billing cycle on {nextBillDate} after which you will be downgraded to the Basic (free) plan.
                </Fragment>);
            }
        } else {
            if (this.isInTrial) {
                message = (<Fragment>
                    <p>
                        Your plan will remain active until the end of the trial period on {nextBillDate}.
                    </p>
                    <p>
                        On that date, your team will be deleted. All members will lose access to Team Plan features, presentations, and data in this workspace. Your credit card will not be charged.
                    </p>
                    <p>
                        Please ensure your team transfers any presentations you'd like to continue to access to another workspace before the end of your trial.
                    </p>
                </Fragment>);
            } else {
                message = (<Fragment>
                    <p>
                        Your plan will remain active until the end of the current billing cycle on {nextBillDate}.
                    </p>
                    <p>
                        On {nextBillDate}, your team will be deleted. All members will lose access to Team Plan features, presentations, and data in this workspace.
                    </p>
                    <p>
                        Please ensure your team transfers any presentations you'd like to continue to access to another workspace before the end of your subscription.
                    </p>
                </Fragment>);
            }
        }

        ShowConfirmationDialog({
            title: `Are you sure you want to cancel your ${this.isInTrial ? "trial" : subscription.product.includes("team") ? "Team plan" : "Pro plan"}?`,
            message,
            cancelButtonLabel: "Never mind",
            okButtonLabel: `Cancel ${this.isInTrial ? "trial" : "plan"}`
        })
            .then(async accept => {
                if (!accept) {
                    return;
                }
                this.setState({ isLoading: true });
                await BillingDataService.cancelSubscription(this.state.subscription.id, this.props.organizationId, { immediate: isInDunning });
                this.loadBillingData();
                ShowDialog(DowngradePlanDialog, { workspaceId: this.props.organizationId || "personal" });
            })
            .catch(err => {
                log.error(err);
                ShowErrorDialog({
                    error: "An error occurred while canceling your subscription",
                    message: (
                        <Fragment>
                            <p>
                                <strong>Error: </strong>
                                {err.message}
                            </p>
                            <p>We apologize for the inconvenience. Please contact us at support@beautiful.ai.</p>
                        </Fragment>
                    )
                });
            });
    };

    showPricingPage = (currentPlan = "basic") => {
        window.open(getPricingPageUrl(app && app.user.hasTakenTrial, app && app.user.has("hasTakenTeamTrial"), currentPlan), "_blank");
    };

    handleReactiveSubscription = async () => {
        const { subscription, paymentMethod } = this.state;
        const customerType = subscription.product.includes("team") ? CustomerType.TEAM : CustomerType.INDIVIDUAL;

        if (!paymentMethod) {
            ShowDialog(ChangePaymentMethodDialog, {
                orgId: this.props.organizationId,
                callback: async () => {
                    await Api.subscriptions.put({
                        orgId: this.props.organizationId,
                        customerType,
                        type: "reactive_subscription"
                    });
                    this.loadBillingData();
                }
            });
            return;
        }

        await Api.subscriptions.put({
            orgId: this.props.organizationId,
            customerType,
            type: "reactive_subscription"
        });
        return this.loadBillingData();
    };

    renderUpgradePlanButton() {
        let { subscription } = this.state;
        if (this.hasSubscription) {
            if (subscription && subscription.cancel_at_period_end) {
                const currentPlan = subscription.product.includes("team") ? "team" : "pro";
                // in canceled subscription period
                return (
                    <YellowButton color="primary" onClick={this.handleReactiveSubscription}>
                        {currentPlan === "team" ? "Reactivate Team Plan" : "Reactivate Pro Plan"}
                    </YellowButton>
                );
            }
        } else {
            return (
                <YellowButton color="primary" onClick={() => this.showPricingPage()}>
                    Upgrade Plan
                </YellowButton>
            );
        }
        return null;
    }

    renderCancelPlanButton() {
        if (this.hasSubscription && !this.canceledSubscription) {
            if (this.isInTrial) {
                return <BlueButton onClick={this.handleCancelSubscription}>Cancel free trial</BlueButton>;
            } else {
                return <BlueButton onClick={this.handleCancelSubscription}>Cancel Plan</BlueButton>;
            }
        }
    }

    renderPlanDescription() {
        const { subscription, quota, isLoading } = this.state;

        if (isLoading) return null;

        if (subscription && subscription.id) {
            let daysRemainingInTrial = Math.ceil(
                (subscription.trialEndsTimestamp * 1000 - new Date().getTime()) / 1000 / 60 / 60 / 24
            );
            const price = subscription.items.data[0].price;
            const isRegularPricing = price.nickname.startsWith("Team-") || price.nickname.startsWith("Pro-");
            return (
                <Fragment>
                    <div className="plan">
                        {subscription.product}
                        <span className="plan-period">({subscription.interval}ly)</span>
                        {subscription.cancel_at_period_end && <span className="canceled-notice">Canceled</span>}
                    </div>
                    {((subscription.status == "trialing" || subscription.status == "active") && !subscription.cancel_at_period_end) && (
                        <>
                            <div style={{
                                fontWeight: 900,
                                fontSize: 14,
                                color: "#828282",
                                marginTop: 10
                            }}>
                                {subscription.interval !== this.nextInterval && (
                                    <span>Scheduled to switch to monthly billing</span>
                                )}
                            </div>
                            {isRegularPricing && (
                                <div
                                    onClick={() =>
                                        ShowDialog(ChangePlanDialog,
                                            {
                                                subscription,
                                                nextInterval: this.nextInterval,
                                                callback: () => this.loadBillingData(),
                                                orgId: this.props.organizationId
                                            })
                                    }
                                    style={{
                                        color: "#159BCD",
                                        cursor: "pointer",
                                        textDecoration: "underline",
                                        fontWeight: 900,
                                        fontSize: 14,
                                        paddingBottom: 40
                                    }}
                                >
                                    {this.nextInterval === "year"
                                        ? "Switch to monthly billing"
                                        : (subscription.interval === this.nextInterval
                                            ? "Save with"
                                            : "Switch back to") + " annual billing"
                                    }
                                </div>
                            )}
                        </>
                    )}
                    {subscription.status == "trialing" && (
                        <div className="plan-info">
                            You have {daysRemainingInTrial} {"day".pluralize(daysRemainingInTrial > 1)} remaining in
                            your free trial
                        </div>
                    )}
                </Fragment>
            );
        } else {
            if (this.props.organizationId === undefined) {
                let userPlan = ds.userPlans.models[0];
                let planName;
                if (userPlan.id == "-LfRhaqvuLMLJ0qs8aNJ" || userPlan.get("name") == "free") {
                    planName = "Basic";
                } else {
                    planName = userPlan.get("name");
                }

                return (
                    <Fragment>
                        <div className="plan">
                            {planName}
                            <span className="plan-period">(free)</span>
                        </div>
                        {
                            quota.slides.limit < APP_UNLIMITED_SLIDE_COUNT
                                ? <Fragment>
                                    <div className="plan-info">
                                        You have used {quota.slides.count} of your {quota.slides.limit} slide limit.
                                    </div>
                                </Fragment>
                                : null
                        }

                    </Fragment>
                );
            } else {
                return <div className="plan">None</div>;
            }
        }
    }

    handleCustomerEmailUpdate = async email => {
        const { subscription } = this.state;
        const customerType = subscription.product.includes("team") ? CustomerType.TEAM : CustomerType.INDIVIDUAL;
        await Api.subscriptions.put({
            customerType,
            email,
            orgId: this.props.organizationId,
            type: "update_customer_email",
        });
        this.loadBillingData();
    };

    handlePromoApplied = () => {
        this.loadBillingData();
    };

    render() {
        let { paymentMethod, subscription, transactionHistoryData, availableReferralCredit } = this.state;
        let isOrganization = this.props.organizationId !== undefined;
        return (
            <UIPane className="billing">
                <UIPaneHeader>{!isOrganization ? "Billing" : "Team Billing"}</UIPaneHeader>
                <UIPaneContents>
                    <Loadable isLoading={this.state.isLoading}>
                        <div className="billing-blocks">
                            <div className="billing-block">
                                <div className="title">Current Plan</div>
                                <div className="contents" style={{ marginBottom: 0 }}>{this.renderPlanDescription()}</div>
                                {!isOrganization && !this.hasSubscription && availableReferralCredit > 0 && (
                                    <div style={{ fontSize: "90%", paddingTop: 10 }}>
                                        <Icon style={{ color: "#fa0", fontSize: "1.25em", verticalAlign: "sub" }}>monetization_on</Icon>{" "}
                                        You have a <b>${availableReferralCredit / 100}</b> referral credit available!
                                    </div>
                                )}
                                <div className="actions">
                                    {this.renderUpgradePlanButton()}
                                    {this.renderCancelPlanButton()}
                                </div>
                            </div>

                            {!this.hasSubscription && (
                                <div className="billing-block">
                                    <div className="title">Your Team</div>
                                    <div className="contents">
                                        Team workspaces allow you to organize your team and control your brand.
                                    </div>
                                    <div className="actions">
                                        <DarkButton onClick={() => this.showPricingPage("basic")}>
                                            Invite your team
                                        </DarkButton>
                                    </div>
                                </div>
                            )}

                            {this.hasSubscription && (
                                <NextPaymentSection
                                    subscription={subscription}
                                    orgId={this.props.organizationId}
                                    nextInterval={this.nextInterval}
                                    availableCredit={availableReferralCredit}
                                    onUpdateEmail={this.handleCustomerEmailUpdate}
                                    onPromoApplied={this.handlePromoApplied}
                                />
                            )}

                            {this.hasSubscription && (
                                <div className="billing-block">
                                    <div className="title">Payment Method</div>
                                    <div className="contents">
                                        {paymentMethod && <PaymentMethod key={paymentMethod.id} {...paymentMethod} fullWidth={false} />}
                                    </div>
                                    <div className="actions">
                                        <BlueButton onClick={this.handleChangePaymentMethod}>
                                            Change Payment Method
                                        </BlueButton>
                                    </div>
                                </div>
                            )}
                        </div>

                        <Gap20 />
                        <TransactionsTable data={transactionHistoryData} />
                    </Loadable>
                </UIPaneContents>
            </UIPane>
        );
    }
}

export default BillingPane;
